import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Alert,
  Container,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { Tabs, Tab, LinearProgress } from '@mui/material';

// Import icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import SecurityIcon from "@mui/icons-material/Security";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import BusinessIcon from "@mui/icons-material/Business";
import TimerIcon from "@mui/icons-material/Timer";
import KeyIcon from "@mui/icons-material/Key";

// Interface for props
interface TokenDecoderProps {
  onBack?: () => void;
}

// Claim definitions and explanations
const CLAIM_DEFINITIONS: Record<string, {
  name: string;
  description: string;
  category: 'authentication' | 'authorization' | 'user' | 'application' | 'security' | 'azure';
  icon: React.ReactNode;
  importance: 'high' | 'medium' | 'low';
  securityNote?: string;
}> = {
  // Standard JWT Claims
  'iss': {
    name: 'Issuer',
    description: 'Identifies the principal that issued the JWT. For Azure AD tokens, this is typically the Azure AD tenant endpoint.',
    category: 'authentication',
    icon: <BusinessIcon />,
    importance: 'high',
    securityNote: 'Always verify the issuer matches your expected Azure AD tenant.'
  },
  'sub': {
    name: 'Subject',
    description: 'Identifies the principal that is the subject of the JWT. In Azure AD, this is usually the user or application object ID.',
    category: 'authentication',
    icon: <AccountCircleIcon />,
    importance: 'high'
  },
  'aud': {
    name: 'Audience',
    description: 'Identifies the recipients that the JWT is intended for. Should match your application\'s client ID or resource URI.',
    category: 'authentication',
    icon: <BusinessIcon />,
    importance: 'high',
    securityNote: 'Token should only be accepted if your application is in the audience.'
  },
  'exp': {
    name: 'Expiration Time',
    description: 'Identifies the expiration time after which the JWT must not be accepted for processing.',
    category: 'security',
    icon: <TimerIcon />,
    importance: 'high',
    securityNote: 'Always check token expiration before accepting the token.'
  },
  'iat': {
    name: 'Issued At',
    description: 'Identifies the time at which the JWT was issued.',
    category: 'security',
    icon: <TimerIcon />,
    importance: 'medium'
  },
  'nbf': {
    name: 'Not Before',
    description: 'Identifies the time before which the JWT must not be accepted for processing.',
    category: 'security',
    icon: <TimerIcon />,
    importance: 'medium'
  },
  'typ': {
    name: 'Token Type',
    description: 'Declares the media type of the JWT. For JWT tokens, this is typically "JWT".',
    category: 'authentication',
    icon: <SecurityIcon />,
    importance: 'medium'
  },
  'alg': {
    name: 'Algorithm',
    description: 'Identifies the cryptographic algorithm used to secure the JWT.',
    category: 'security',
    icon: <SecurityIcon />,
    importance: 'high',
    securityNote: 'Ensure the algorithm is approved for your security requirements.'
  },
  
  // Azure AD Specific Claims
  'tid': {
    name: 'Tenant ID',
    description: 'The Azure AD tenant ID that issued the token. Unique identifier for the Azure AD directory.',
    category: 'azure',
    icon: <BusinessIcon />,
    importance: 'high'
  },
  'oid': {
    name: 'Object ID',
    description: 'The immutable identifier for the user or application in Azure AD. This ID uniquely identifies the user across applications.',
    category: 'azure',
    icon: <AccountCircleIcon />,
    importance: 'high'
  },
  'upn': {
    name: 'User Principal Name',
    description: 'The user principal name of the user. This is typically the user\'s email address in Azure AD.',
    category: 'user',
    icon: <AccountCircleIcon />,
    importance: 'medium'
  },
  'unique_name': {
    name: 'Unique Name',
    description: 'A human-readable value that identifies the subject of the token. Often the same as UPN.',
    category: 'user',
    icon: <AccountCircleIcon />,
    importance: 'medium'
  },
  'preferred_username': {
    name: 'Preferred Username',
    description: 'The primary username that represents the user. Could be an email address, phone number, or generic username.',
    category: 'user',
    icon: <AccountCircleIcon />,
    importance: 'medium'
  },
  'name': {
    name: 'Display Name',
    description: 'The display name for the user as stored in Azure AD.',
    category: 'user',
    icon: <AccountCircleIcon />,
    importance: 'low'
  },
  'given_name': {
    name: 'Given Name',
    description: 'The first name of the user as stored in Azure AD.',
    category: 'user',
    icon: <AccountCircleIcon />,
    importance: 'low'
  },
  'family_name': {
    name: 'Family Name',
    description: 'The last name of the user as stored in Azure AD.',
    category: 'user',
    icon: <AccountCircleIcon />,
    importance: 'low'
  },
  'scp': {
    name: 'Scopes',
    description: 'The scopes granted to the application. Defines what permissions the token has.',
    category: 'authorization',
    icon: <KeyIcon />,
    importance: 'high',
    securityNote: 'Review scopes to ensure they match your application\'s requirements.'
  },
  'roles': {
    name: 'Application Roles',
    description: 'The application roles assigned to the user or application. Used for role-based authorization.',
    category: 'authorization',
    icon: <KeyIcon />,
    importance: 'high'
  },
  'wids': {
    name: 'Directory Roles',
    description: 'The directory roles assigned to the user in Azure AD (e.g., Global Administrator).',
    category: 'authorization',
    icon: <KeyIcon />,
    importance: 'high',
    securityNote: 'High-privilege directory roles should be carefully monitored.'
  },
  'appid': {
    name: 'Application ID',
    description: 'The application ID of the client application that requested the token.',
    category: 'application',
    icon: <BusinessIcon />,
    importance: 'medium'
  },
  'ver': {
    name: 'Token Version',
    description: 'The version of the Azure AD token schema used.',
    category: 'azure',
    icon: <InfoOutlinedIcon />,
    importance: 'low'
  },
  'acr': {
    name: 'Authentication Context Class Reference',
    description: 'Indicates how the client was authenticated (e.g., password, MFA).',
    category: 'security',
    icon: <SecurityIcon />,
    importance: 'high',
    securityNote: 'Higher ACR values indicate stronger authentication methods.'
  },
  'amr': {
    name: 'Authentication Methods References',
    description: 'Identifies how the subject of the token was authenticated (pwd, mfa, etc.).',
    category: 'security',
    icon: <SecurityIcon />,
    importance: 'high'
  },
  'azp': {
    name: 'Authorized Party',
    description: 'The application ID of the client application. Used in scenarios where client ID differs from audience.',
    category: 'application',
    icon: <BusinessIcon />,
    importance: 'medium'
  },
  'acct': {
    name: 'Account Type',
    description: 'Numeric indicator of the account type for the signed-in user. Typically 0 = consumer (MSA) and 1 = organizational (work/school). Informational; avoid using for authorization.',
    category: 'user',
    icon: <AccountCircleIcon />,
    importance: 'low'
  },
  'acrs': {
    name: 'Auth Context Class References',
    description: 'Set of additional authentication context/class references applied during sign-in (e.g., policy identifiers).',
    category: 'security',
    icon: <SecurityIcon />,
    importance: 'medium'
  },
  'appidacr': {
    name: 'App Auth Method (appidacr)',
    description: 'Indicates how the client application authenticated with the identity platform. Common values: 0 = public client (no secret/cert), 1 = confidential client (secret or certificate).',
    category: 'authentication',
    icon: <KeyIcon />,
    importance: 'high',
    securityNote: 'Confidential clients should report 1; SPAs normally report 0.'
  },
  'idtyp': {
    name: 'Identity Type',
    description: 'Type of identity represented by the token. Common values include "user" or "app".',
    category: 'authentication',
    icon: <SecurityIcon />,
    importance: 'high'
  },
  'platf': {
    name: 'Client Platform',
    description: 'Platform identifier for the requesting client. Informational/telemetry; not intended for authorization.',
    category: 'application',
    icon: <InfoOutlinedIcon />,
    importance: 'low'
  },
  'puid': {
    name: 'Persistent User ID (puid)',
    description: 'Stable user identifier used internally by Microsoft Entra. Do not rely on for app-level authorization.',
    category: 'user',
    icon: <AccountCircleIcon />,
    importance: 'medium'
  },
  'rh': {
    name: 'Refresh Session Handle',
    description: 'Opaque handle related to refresh token/session management. Diagnostic only; do not log in production.',
    category: 'security',
    icon: <InfoOutlinedIcon />,
    importance: 'low'
  },
  'sid': {
    name: 'Session ID',
    description: 'Identifier for the sign-in session. Useful for single sign-out and diagnostics; not for authorization.',
    category: 'security',
    icon: <SecurityIcon />,
    importance: 'low'
  },
  'signin_state': {
    name: 'Sign-in State',
    description: 'Space-separated hints about sign-in context (e.g., device managed, KMSI). Diagnostic only.',
    category: 'security',
    icon: <InfoOutlinedIcon />,
    importance: 'low'
  },
  'uti': {
    name: 'Unique Token Identifier',
    description: 'Unique per-token value to help detect replay and for diagnostics.',
    category: 'security',
    icon: <SecurityIcon />,
    importance: 'medium'
  },
  'azpacr': {
    name: 'Authorized Party Auth Method (azpacr)',
    description: 'Indicates how the authorized party (azp) authenticated with Azure AD. Common values: 0 = public client, 1 = confidential client. Used in multi-tier or on-behalf-of flows.',
    category: 'authentication',
    icon: <KeyIcon />,
    importance: 'medium',
    securityNote: 'Confidential clients should report 1; public clients (like SPAs) report 0.'
  }
};  

const MONO_STACK =
  `ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace`;

// Get claim category color
const getCategoryColor = (category: string) => {
  switch (category) {
    case 'authentication': return '#1976d2';
    case 'authorization': return '#388e3c';
    case 'user': return '#7b1fa2';
    case 'application': return '#f57c00';
    case 'security': return '#d32f2f';
    case 'azure': return '#0078d4';
    default: return '#616161';
  }
};

// Claims Inspector Component
const ClaimsInspector: React.FC<{
  claim: string;
  value: any;
  open: boolean;
  onClose: () => void;
}> = ({ claim, value, open, onClose }) => {
  const definition = CLAIM_DEFINITIONS[claim];
  const [copied, setCopied] = useState(false);

  const handleCopyValue = () => {
    const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#f5f7fa'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {definition?.icon && (
            <Box sx={{ 
              mr: 2, 
              color: getCategoryColor(definition.category),
              display: 'flex',
              alignItems: 'center'
            }}>
              {definition.icon}
            </Box>
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {definition?.name || claim}
            </Typography>
            <Typography variant="caption" sx={{ 
              color: getCategoryColor(definition?.category || 'default'),
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.1em'
            }}>
              {definition?.category || 'custom'} claim
            </Typography>
          </Box>
        </Box>
        
        {definition && (
          <Chip 
            label={definition.importance}
            color={definition.importance === 'high' ? 'error' : 
                  definition.importance === 'medium' ? 'warning' : 'default'}
            size="small"
            sx={{ ml: 1 }}
          />
        )}
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        {definition ? (
          <>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
              {definition.description}
            </Typography>
            
            {definition.securityNote && (
              <Alert 
                severity="warning" 
                sx={{ mb: 3, borderRadius: 2 }}
                icon={<WarningIcon />}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Security Consideration
                </Typography>
                {definition.securityNote}
              </Alert>
            )}
          </>
        ) : (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            This appears to be a custom claim. Custom claims are application-specific and 
            may contain business logic data or additional user attributes.
          </Alert>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Current Value
        </Typography>
        
        <Paper 
          variant="outlined"
          sx={{ 
            p: 2, 
            borderRadius: 2, 
            backgroundColor: "#f8f9fa",
            position: 'relative'
          }}
        >
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <Tooltip title={copied ? "Copied" : "Copy value"}>
              <IconButton size="small" onClick={handleCopyValue} sx={{ color: copied ? 'success.main' : 'action.active' }}>
                {copied ? <DoneIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
          </Box>
          <pre 
            style={{ 
              margin: 0, 
              fontSize: '0.875rem',
              fontFamily: MONO_STACK,
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word'
            }}
          >
            {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
          </pre>
        </Paper>
        
        {/* Special handling for array values like scopes */}
        {Array.isArray(value) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Array Values ({value.length} items)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {value.map((item: any, index: number) => (
                <Chip 
                  key={index}
                  label={String(item)}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Box>
          </Box>
        )}
        
        {/* Special handling for scope strings */}
        {claim === 'scp' && typeof value === 'string' && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Individual Scopes
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {value.split(' ').filter(Boolean).map((scope: string, index: number) => (
                <Chip 
                  key={index}
                  label={scope}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, backgroundColor: '#f5f7fa' }}>
        <Button onClick={onClose} variant="contained" sx={{ borderRadius: 2 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Interactive Claim Component
const InteractiveClaim: React.FC<{
  claim: string;
  value: any;
  onClaimClick: (claim: string, value: any) => void;
}> = ({ claim, value, onClaimClick }) => {
  const definition = CLAIM_DEFINITIONS[claim];
  const hasDefinition = Boolean(definition);
  
  return (
    <Grid container spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
      <Grid item xs={12} sm={3} md={2}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600, 
              color: 'text.secondary',
              cursor: hasDefinition ? 'pointer' : 'default',
              '&:hover': hasDefinition ? {
                color: 'primary.main',
                textDecoration: 'underline'
              } : {}
            }}
            onClick={() => hasDefinition && onClaimClick(claim, value)}
          >
            {claim}
          </Typography>
          
          {hasDefinition && (
            <Tooltip title="Click for detailed explanation" arrow>
              <IconButton 
                size="small" 
                sx={{ 
                  ml: 0.5, 
                  color: getCategoryColor(definition.category),
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }}
                onClick={() => onClaimClick(claim, value)}
              >
                <HelpOutlineIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Tooltip>
          )}
          
          {definition?.importance === 'high' && (
            <Badge
              badgeContent=""
              color="error"
              variant="dot"
              sx={{ ml: 0.5 }}
            />
          )}
        </Box>
      </Grid>
      <Grid item xs={12} sm={9} md={10}>
        {claim === 'scp' && typeof value === 'string' ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {value.split(" ").filter(Boolean).map((scope: string, index: number) => (
              <Chip 
                key={index} 
                label={scope} 
                size="small" 
                variant="outlined"
                color="primary"
                sx={{ 
                  fontSize: '0.8rem',
                  '& .MuiChip-label': { fontFamily: MONO_STACK }
                }}
              />
            ))}
          </Box>
        ) : Array.isArray(value) ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {value.map((item: any, index: number) => (
              <Chip 
                key={index} 
                label={String(item)} 
                size="small" 
                variant="outlined"
                color="secondary"
                sx={{ 
                  fontSize: '0.8rem',
                  '& .MuiChip-label': { fontFamily: MONO_STACK }
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography 
            variant="body2" 
            sx={{ 
              wordBreak: 'break-word',
              fontFamily: MONO_STACK,
              fontSize: '0.85rem',
              letterSpacing: '0.02em'
            }}
          >
            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

// Utility functions
function decodeBase64Url(segment: string): string {
  // Convert from base64url and add padding if necessary
  let base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const paddingNeeded = 4 - (base64.length % 4);
  if (paddingNeeded !== 4) {
    base64 += "=".repeat(paddingNeeded);
  }
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  );
  return jsonPayload;
}

function decodeToken(token: string) {
  try {
    const segments = token.split(".");
    if (segments.length !== 3) {
      throw new Error("JWT must have three segments (header, payload, signature).");
    }
    const headerDecoded = JSON.parse(decodeBase64Url(segments[0]));
    const payloadDecoded = JSON.parse(decodeBase64Url(segments[1]));

    return { header: headerDecoded, payload: payloadDecoded };
  } catch (err) {
    console.error("Token decoding failed", err);
    return null;
  }
}

async function verifyRS256Signature(token: string, jwk: any): Promise<boolean> {
  try {
    const [headerB64, payloadB64, signatureB64] = token.split('.');
    if (!headerB64 || !payloadB64 || !signatureB64) return false;
    if (!jwk || jwk.kty !== 'RSA' || !jwk.n || !jwk.e) return false;
    const sigB64 = signatureB64.replace(/-/g,'+').replace(/_/g,'/');
    const pad = sigB64.length % 4; const sigPadded = sigB64 + (pad? '='.repeat(4-pad):'');
    const sigBytes = Uint8Array.from(atob(sigPadded), c=> c.charCodeAt(0));
    const key = await crypto.subtle.importKey('jwk', { kty:'RSA', n:jwk.n, e:jwk.e, alg:'RS256', ext:true }, { name:'RSASSA-PKCS1-v1_5', hash:'SHA-256' }, false, ['verify']);
    const data = new TextEncoder().encode(`${headerB64}.${payloadB64}`);
    return await crypto.subtle.verify('RSASSA-PKCS1-v1_5', key, sigBytes, data);
  } catch (e) {
    console.warn('RS256 verify failed', e); return false;
  }
}

const TokenDecoder: React.FC<TokenDecoderProps> = ({ onBack }) => {
  const [tokenInput, setTokenInput] = useState<string>("");
  const [decodedData, setDecodedData] = useState<{ header: any; payload: any } | null>(null);
  const [error, setError] = useState<string>("");
  const [headerCopySuccess, setHeaderCopySuccess] = useState<boolean>(false);
  const [payloadCopySuccess, setPayloadCopySuccess] = useState<boolean>(false);

  // Claims inspector state
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<{claim: string, value: any} | null>(null);
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [summaryTab, setSummaryTab] = useState<'summary' | 'analysis'>('summary');
  const [jwksInput, setJwksInput] = useState('');
  const [jwksParseError, setJwksParseError] = useState('');
  const [jwksKeys, setJwksKeys] = useState<any[] | null>(null);
  const [keyMatches, setKeyMatches] = useState<any[]>([]);
  const [verifying, setVerifying] = useState(false);
  const [signatureValid, setSignatureValid] = useState<boolean | null>(null);
  const [jwksFetching, setJwksFetching] = useState(false);
  const [jwksFetchError, setJwksFetchError] = useState<string>('');
  const [jwksFetchSuccess, setJwksFetchSuccess] = useState<string>('');
  const [jwksAutoFetched, setJwksAutoFetched] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);

  // Handle decode
  const handleDecode = () => {
    if (!tokenInput.trim()) {
      setError("Please provide a JWT token.");
      setDecodedData(null);
      return;
    }
    const result = decodeToken(tokenInput.trim());
    if (result) {
      setDecodedData(result);
      setError("");
    } else {
      setDecodedData(null);
      setError("Failed to decode token. Check console for details.");
    }
  };

  // Handle clear
  const handleClear = () => {
    setTokenInput("");
    setDecodedData(null);
    setError("");
  };

  // Handle copy to clipboard
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    // no-op: use specific setters where needed
  };

  // Format timestamp to human-readable date
  const formatDate = (timestamp: number) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleString();
  };

  // Get token expiration status
  const getTokenStatus = () => {
    if (!decodedData?.payload?.exp) return null;
    const now = Math.floor(Date.now() / 1000);
    const exp = decodedData.payload.exp;
    if (exp > now) {
      // Calculate time remaining
      const diff = exp - now;
      const days = Math.floor(diff / (60 * 60 * 24));
      const hours = Math.floor((diff % (60 * 60 * 24)) / (60 * 60));
      const mins = Math.floor((diff % (60 * 60)) / 60);
      
      let timeRemaining = "";
      if (days > 0) timeRemaining = `${days}d ${hours}h remaining`;
      else if (hours > 0) timeRemaining = `${hours}h ${mins}m remaining`;
      else timeRemaining = `${mins}m remaining`;
      
      return { status: "valid", message: timeRemaining };
    } else {
      return { status: "expired", message: "Expired" };
    }
  };

  const tokenStatus = decodedData ? getTokenStatus() : null;

  // Handle claim click
  const handleClaimClick = (claim: string, value: any) => {
    setSelectedClaim({ claim, value });
    setInspectorOpen(true);
  };

  // Get security analysis
  const getSecurityAnalysis = () => {
    if (!decodedData) return null;
    
    const issues: Array<{type: 'error' | 'warning' | 'info', message: string}> = [];
    const payload = decodedData.payload;
    
    // Check token expiration
    if (payload.exp) {
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp < now) {
        issues.push({
          type: 'error',
          message: 'Token has expired and should not be accepted'
        });
      } else if (payload.exp - now < 300) { // Less than 5 minutes
        issues.push({
          type: 'warning',
          message: 'Token expires soon (less than 5 minutes)'
        });
      }
    }
    
    // Check for high-privilege roles
    if (payload.wids && Array.isArray(payload.wids)) {
      const adminRoles = payload.wids.filter((role: string) => 
        role === '62e90394-69f5-4237-9190-012177145e10' || // Global Administrator
        role === '194ae4cb-b126-40b2-bd5b-6091b380977d'    // Security Administrator
      );
      if (adminRoles.length > 0) {
        issues.push({
          type: 'warning',
          message: 'Token contains high-privilege directory roles'
        });
      }
    }
    
    // Check authentication method
    if (payload.amr && Array.isArray(payload.amr)) {
      if (!payload.amr.includes('mfa')) {
        issues.push({
          type: 'info',
          message: 'Token was issued without multi-factor authentication'
        });
      }
    }
    
    return issues;
  };

  const securityIssues = decodedData ? getSecurityAnalysis() : null;

  const parseJwks = () => {
    setJwksParseError(''); setSignatureValid(null); setKeyMatches([]); setJwksAutoFetched(false);
    if (!jwksInput.trim()) { setJwksKeys(null); return; }
    try {
      const json = JSON.parse(jwksInput.trim());
      const keys = Array.isArray(json.keys) ? json.keys : [];
      setJwksKeys(keys);
      if (decodedData?.header && keys.length) {
        const matches = keys.filter((k:any) => (
          (k.kid && decodedData.header.kid && k.kid === decodedData.header.kid) ||
          (k.alg && decodedData.header.alg && k.alg === decodedData.header.alg)
        ));
        setKeyMatches(matches);
      }
    } catch (e:any) {
      setJwksParseError(e.message || 'Failed to parse JWKS JSON');
      setJwksKeys(null);
    }
  };

  // Allow parsing externally fetched JWKS content
  const parseJwksContent = (content: string) => {
    try {
      const json = JSON.parse(content);
      const keys = Array.isArray(json.keys) ? json.keys : [];
      setJwksKeys(keys);
      if (decodedData?.header && keys.length) {
        const matches = keys.filter((k:any) => (
          (k.kid && decodedData.header.kid && k.kid === decodedData.header.kid) ||
          (k.alg && decodedData.header.alg && k.alg === decodedData.header.alg)
        ));
        setKeyMatches(matches);
      }
    } catch (e:any) {
      setJwksParseError(e.message || 'Failed to parse JWKS JSON');
      setJwksKeys(null);
    }
  };

  const fetchJwksFromIssuer = async () => {
    setJwksFetchError(''); setJwksFetchSuccess(''); setSignatureValid(null); setKeyMatches([]);
    if (!decodedData?.payload?.iss) { setJwksFetchError('No iss claim found in token'); return; }
    let issuer: string = decodedData.payload.iss;
    if (issuer.endsWith('/')) issuer = issuer.slice(0, -1);
    // Try building discovery URL (support tokens missing /v2.0 well-known nuance)
    const candidateUrls = [
      `${issuer}/.well-known/openid-configuration`,
      issuer.includes('/v2.0') ? issuer.replace('/v2.0','') + '/v2.0/.well-known/openid-configuration' : `${issuer}/v2.0/.well-known/openid-configuration`
    ];
    let discoveryJson: any = null;
    setJwksFetching(true);
    for (const url of candidateUrls) {
      try {
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) continue;
        discoveryJson = await res.json();
        break;
      } catch { /* try next */ }
    }
    if (!discoveryJson) { setJwksFetching(false); setJwksFetchError('Failed to load discovery document from iss'); return; }
    if (!discoveryJson.jwks_uri) { setJwksFetching(false); setJwksFetchError('Discovery did not contain jwks_uri'); return; }
    try {
      const jwksRes = await fetch(discoveryJson.jwks_uri, { cache: 'no-store' });
      if (!jwksRes.ok) throw new Error(`JWKS fetch failed (${jwksRes.status})`);
      const text = await jwksRes.text();
      setJwksInput(text);
      parseJwksContent(text);
      setJwksFetchSuccess('JWKS fetched from issuer successfully');
      setJwksAutoFetched(true);
    } catch (e:any) {
      setJwksFetchError(e.message || 'Failed to fetch JWKS from jwks_uri');
    } finally {
      setJwksFetching(false);
    }
  };

  const verifySignature = async () => {
    if (!decodedData || !jwksKeys) return;
    setVerifying(true); setSignatureValid(null);
    try {
      const header = decodedData.header;
      if (header.alg !== 'RS256') { setSignatureValid(false); return; }
      let candidate = keyMatches.find(k => k.kid === header.kid);
      if (!candidate && jwksKeys.length === 1) candidate = jwksKeys[0];
      if (!candidate) { setSignatureValid(false); return; }
      const ok = await verifyRS256Signature(tokenInput.trim(), candidate);
      setSignatureValid(ok);
    } finally { setVerifying(false); }
  };

  const tokenAnalysis = decodedData ? (() => {
    const p = decodedData.payload || {};
    const now = Math.floor(Date.now()/1000);
    const classification: string[] = [];
    if (p.scp || p.roles) classification.push('access token candidate');
    if (p.oid && p.tid && !p.scp) classification.push('id token candidate');
    if (decodedData.header?.typ && decodedData.header.typ.toLowerCase().includes('at+jwt')) classification.push('access token (at+jwt)');
    const timing: any = {};
    if (p.iat) timing.issued = new Date(p.iat*1000).toLocaleString();
    if (p.nbf) timing.notBefore = new Date(p.nbf*1000).toLocaleString();
    if (p.exp) { timing.expires = new Date(p.exp*1000).toLocaleString(); timing.ttlSeconds = p.exp - now; }
    const scopes = typeof p.scp === 'string' ? p.scp.split(' ').filter(Boolean) : [];
    const roles = Array.isArray(p.roles) ? p.roles : [];
    return { classification, timing, scopes, roles };
  })() : null;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          {onBack && (
            <Button 
              variant="outlined" 
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{
                mr: 2,
                borderRadius: 2,
                pl: 1.5,
                color: "text.secondary",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  borderColor: "primary.main",
                  color: "primary.main"
                }
              }}
            >
              Back
            </Button>
          )}
          
          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              JWT Token Decoder
            </Typography>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Decode and inspect tokens for authentication debugging
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Token Input Card */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Typography
            variant="h6"
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid rgba(0,0,0,0.12)',
              backgroundColor: '#f9f9f9',
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <VpnKeyIcon sx={{ mr: 1, color: 'primary.main' }} />
            Token Input
          </Typography>
          
          <Box sx={{ p: 3 }}>
            <TextField
              label="Paste JWT Token Here"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c"
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: MONO_STACK,
                  fontSize: '0.875rem'
                }
              }}
              InputProps={{
                endAdornment: tokenInput && (
                  <IconButton
                    size="small"
                    onClick={() => setTokenInput("")}
                    sx={{ mr: 0.5 }}
                  >
                    <ClearIcon />
                  </IconButton>
                )
              }}
            />

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDecode}
                startIcon={<SearchIcon />}
                sx={{ 
                  borderRadius: 2,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)'
                  }
                }}
              >
                Decode Token
              </Button>
              
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClear}
                startIcon={<ClearIcon />}
                sx={{ borderRadius: 2 }}
              >
                Clear Input
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Display Decoded Token */}
      {decodedData && (
        <>
          {/* Token Summary Card - Enhanced */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: '1px solid rgba(0,0,0,0.12)',
                  backgroundColor: '#f9f9f9',
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <VerifiedUserIcon sx={{ mr: 1, color: 'primary.main' }} />
                  Token Summary
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tokenStatus && (
                    <Chip 
                      label={tokenStatus.message}
                      color={tokenStatus.status === "valid" ? "success" : "error"}
                      size="small"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  )}
                  <Tooltip title={summaryExpanded ? 'Collapse' : 'Expand'}>
                    <IconButton size="small" onClick={() => setSummaryExpanded(v => !v)}>
                      {summaryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Typography>
              
              <Collapse in={summaryExpanded} timeout="auto">
                <Box sx={{ p: 3 }}>
                  <Tabs value={summaryTab} onChange={(_,v)=> setSummaryTab(v)} sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Tab label="Token Summary" value="summary" />
                    <Tab label="Token Analysis" value="analysis" />
                  </Tabs>

                  {summaryTab === 'summary' && (
                    <>
                      {securityIssues && securityIssues.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'error.main' }}>
                            Security Analysis
                          </Typography>
                          {securityIssues.map((issue, index) => (
                            <Alert key={index} severity={issue.type} sx={{ mb: 1, borderRadius: 1 }} icon={issue.type === 'error' ? <ErrorIcon /> : issue.type === 'warning' ? <WarningIcon /> : <InfoOutlinedIcon />}>{issue.message}</Alert>
                          ))}
                        </Box>
                      )}
                      <Box sx={{ p: 3, backgroundColor: 'rgba(227, 242, 253, 0.5)', borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'primary.dark' }}>
                          Token Properties
                          <Tooltip title="Click on any claim for detailed explanation"><IconButton size="small" sx={{ ml: 1 }}><HelpOutlineIcon sx={{ fontSize: '1rem' }} /></IconButton></Tooltip>
                        </Typography>
                        <InteractiveClaim claim="typ" value={decodedData.header.typ || "Unknown"} onClaimClick={handleClaimClick} />
                        <InteractiveClaim claim="alg" value={decodedData.header.alg || "Unknown"} onClaimClick={handleClaimClick} />
                        <InteractiveClaim claim="iss" value={decodedData.payload.iss || "Not specified"} onClaimClick={handleClaimClick} />
                        <InteractiveClaim claim="sub" value={decodedData.payload.sub || "Not specified"} onClaimClick={handleClaimClick} />
                        <InteractiveClaim claim="aud" value={decodedData.payload.aud || "Not specified"} onClaimClick={handleClaimClick} />
                        <InteractiveClaim claim="iat" value={decodedData.payload.iat ? formatDate(decodedData.payload.iat) : "Not specified"} onClaimClick={handleClaimClick} />
                        <InteractiveClaim claim="exp" value={decodedData.payload.exp ? formatDate(decodedData.payload.exp) : "Not specified"} onClaimClick={handleClaimClick} />
                        {decodedData.payload.tid && <InteractiveClaim claim="tid" value={decodedData.payload.tid} onClaimClick={handleClaimClick} />}
                        {decodedData.payload.oid && <InteractiveClaim claim="oid" value={decodedData.payload.oid} onClaimClick={handleClaimClick} />}
                        {decodedData.payload.upn && <InteractiveClaim claim="upn" value={decodedData.payload.upn} onClaimClick={handleClaimClick} />}
                        {decodedData.payload.scp && <InteractiveClaim claim="scp" value={decodedData.payload.scp} onClaimClick={handleClaimClick} />}
                        {decodedData.payload.roles && <InteractiveClaim claim="roles" value={decodedData.payload.roles} onClaimClick={handleClaimClick} />}
                        {decodedData.payload.wids && <InteractiveClaim claim="wids" value={decodedData.payload.wids} onClaimClick={handleClaimClick} />}
                      </Box>
                    </>
                  )}

                  {summaryTab === 'analysis' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>Paste JWKS (JSON Web Key Set) to enable key matching and signature verification. All verification happens locally.</Alert>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>JWKS JSON</Typography>
                        <TextField value={jwksInput} onChange={e=> setJwksInput(e.target.value)} placeholder='{"keys":[{"kty":"RSA","kid":"...","n":"...","e":"AQAB"}]}' multiline minRows={4} fullWidth size="small" sx={{ fontFamily: MONO_STACK }} />
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          <Tooltip title={decodedData?.payload?.iss ? 'Fetch JWKS via issuer discovery' : 'iss claim not present'}>
                            <span>
                              <Button variant="contained" size="small" onClick={fetchJwksFromIssuer} disabled={!decodedData?.payload?.iss || jwksFetching} sx={{ borderRadius: 2 }}>
                                {jwksFetching ? 'Fetching…' : 'Fetch from iss'}
                              </Button>
                            </span>
                          </Tooltip>
                          <Button variant="outlined" size="small" onClick={parseJwks} disabled={!jwksInput.trim() || jwksAutoFetched} sx={{ borderRadius: 2 }}>Parse JWKS</Button>
                          <Button variant="text" size="small" onClick={()=> { setJwksInput(''); setJwksKeys(null); setKeyMatches([]); setSignatureValid(null); setJwksParseError(''); setJwksFetchError(''); setJwksFetchSuccess(''); setJwksAutoFetched(false); }} sx={{ borderRadius: 2 }}>Clear JWKS</Button>
                          <Tooltip title={!jwksKeys ? 'Parse JWKS first' : 'Verify signature (RS256)'}>
                            <span>
                              <Button variant="contained" size="small" disabled={!jwksKeys || verifying} onClick={verifySignature} sx={{ borderRadius: 2 }}>{verifying ? 'Verifying...' : 'Verify Signature'}</Button>
                            </span>
                          </Tooltip>
                          {verifying && <LinearProgress sx={{ flex: 1, minWidth: 120, borderRadius: 1 }} />}
                        </Box>
                        {jwksParseError && <Alert severity="error" sx={{ mt: 1, borderRadius: 1 }}>{jwksParseError}</Alert>}
                        {jwksFetchError && <Alert severity="error" sx={{ mt: 1, borderRadius: 1 }}>{jwksFetchError}</Alert>}
                        {jwksFetchSuccess && <Alert severity="success" sx={{ mt: 1, borderRadius: 1 }}>{jwksFetchSuccess}</Alert>}
                      </Box>
                      {jwksKeys && <Alert severity="success" sx={{ borderRadius: 2 }}>Loaded {jwksKeys.length} key{jwksKeys.length!==1 && 's'}. {keyMatches.length? `${keyMatches.length} key match heuristic(s).` : 'No kid/alg matches yet.'}</Alert>}
                      {signatureValid !== null && <Alert severity={signatureValid? 'success':'error'} sx={{ borderRadius: 2 }}>Signature {signatureValid? 'valid':'invalid'} (RS256)</Alert>}
                      {tokenAnalysis && (
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Token Analysis</Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>Classification</Typography>
                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {tokenAnalysis.classification.length ? tokenAnalysis.classification.map((c,i)=>(<Chip key={i} size="small" label={c} color={c.includes('access')? 'primary':'secondary'} />)) : <Typography variant="body2">No heuristic classification</Typography>}
                                </Box>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>Timing</Typography>
                                <List dense>
                                  {Object.entries(tokenAnalysis.timing).map(([k,v]) => (
                                    <ListItem key={k} sx={{ py: 0.5 }}>
                                      <ListItemIcon sx={{ minWidth: 32 }}><TimerIcon fontSize="small" /></ListItemIcon>
                                      <ListItemText primaryTypographyProps={{ fontSize: '0.8rem' }} primary={`${k}: ${typeof v === 'number' ? v + 's' : v}`} />
                                    </ListItem>
                                  ))}
                                </List>
                                {decodedData?.payload?.exp && decodedData.payload.exp < Math.floor(Date.now()/1000) && (
                                  <Alert severity="error" icon={<ErrorIcon fontSize="small" />} sx={{ mt: 1, borderRadius: 1, p: 0.75 }}>
                                    Token expired - must not be accepted
                                  </Alert>
                                )}
                              </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>Scopes / Roles</Typography>
                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {tokenAnalysis.scopes.map((s: string, i: number)=>(<Chip key={i} size="small" label={s} variant="outlined" color="primary" />))}
                                  {tokenAnalysis.roles.map((r: string, i: number)=>(<Chip key={'r'+i} size="small" label={r} variant="outlined" color="secondary" />))}
                                  {!tokenAnalysis.scopes.length && !tokenAnalysis.roles.length && <Typography variant="body2">None present</Typography>}
                                </Box>
                              </Paper>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Raw Header & Payload</Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, background: '#fafafa' }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>Header</Typography>
                              <pre style={{ margin: 0, fontFamily: MONO_STACK, fontSize: '0.7rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(decodedData.header, null, 2)}</pre>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, background: '#fafafa' }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>Payload</Typography>
                              <pre style={{ margin: 0, fontFamily: MONO_STACK, fontSize: '0.7rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(decodedData.payload, null, 2)}</pre>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </CardContent>
          </Card>
          
          {/* Detailed Token Data Card */}
          

          {/* All Claims Explorer */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: '1px solid rgba(0,0,0,0.12)',
                  backgroundColor: '#f9f9f9',
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <SearchIcon sx={{ mr: 1, color: 'primary.main' }} />
                Interactive Claims Explorer
                <Tooltip title="Click on any claim name for detailed explanation">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              </Typography>
              
              <Box sx={{ p: 3 }}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Header Claims
                      </Typography>
                      <Tooltip title={headerCopySuccess ? "Copied" : "Copy header JSON"}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(JSON.stringify(decodedData.header, null, 2));
                            setHeaderCopySuccess(true);
                            setTimeout(() => setHeaderCopySuccess(false), 1500);
                          }}
                          sx={{ color: headerCopySuccess ? 'success.main' : 'action.active' }}
                        >
                          {headerCopySuccess ? <DoneIcon /> : <ContentCopyIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {Object.entries(decodedData.header).map(([key, value]) => (
                      <InteractiveClaim 
                        key={key}
                        claim={key} 
                        value={value} 
                        onClaimClick={handleClaimClick} 
                      />
                    ))}
                  </AccordionDetails>
                </Accordion>
                
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Payload Claims
                        </Typography>
                        <Chip 
                          label={`${Object.keys(decodedData.payload).length} claims`}
                          size="small"
                          sx={{ ml: 2 }}
                        />
                      </Box>
                      <Tooltip title={payloadCopySuccess ? "Copied" : "Copy payload JSON"}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(JSON.stringify(decodedData.payload, null, 2));
                            setPayloadCopySuccess(true);
                            setTimeout(() => setPayloadCopySuccess(false), 1500);
                          }}
                          sx={{ color: payloadCopySuccess ? 'success.main' : 'action.active' }}
                        >
                          {payloadCopySuccess ? <DoneIcon /> : <ContentCopyIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {Object.entries(decodedData.payload).map(([key, value]) => (
                      <InteractiveClaim 
                        key={key}
                        claim={key} 
                        value={value} 
                        onClaimClick={handleClaimClick} 
                      />
                    ))}
                  </AccordionDetails>
                </Accordion>
              </Box>
            </CardContent>
          </Card>          
        </>
      )}

      {/* Claims Inspector Dialog */}
      {selectedClaim && (
        <ClaimsInspector
          claim={selectedClaim.claim}
          value={selectedClaim.value}
          open={inspectorOpen}
          onClose={() => {
            setInspectorOpen(false);
            setSelectedClaim(null);
          }}
        />
      )}

      {/* About Section - Expandable */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3, mt: 3 }}>
        <Accordion expanded={aboutExpanded} onChange={() => setAboutExpanded(!aboutExpanded)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                About JWT Token Decoder
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                This tool allows you to decode and inspect JWT (JSON Web Token) tokens for authentication and authorization debugging. 
                You can analyze token structure, claims, expiration, and optionally verify signatures using JWKS.
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Understanding JWT Structure:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  JWT tokens consist of three parts separated by dots (.):
                </Typography>
                <Box component="ul" sx={{ mt: 0.5, mb: 0.5, pl: 3 }}>
                  <li><Typography variant="body2" color="text.secondary"><strong>Header:</strong> Contains metadata about the token (algorithm, type)</Typography></li>
                  <li><Typography variant="body2" color="text.secondary"><strong>Payload:</strong> Contains the claims (user info, permissions, expiration)</Typography></li>
                  <li><Typography variant="body2" color="text.secondary"><strong>Signature:</strong> Cryptographic signature to verify authenticity</Typography></li>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Each part is Base64Url encoded, making tokens safe for URLs and HTTP headers.
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Common JWT Claims:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', backgroundColor: 'rgba(227, 242, 253, 0.3)' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>STANDARD CLAIMS</Typography>
                      <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
                        <li><Typography variant="body2"><strong>iss:</strong> Issuer (who created the token)</Typography></li>
                        <li><Typography variant="body2"><strong>sub:</strong> Subject (who the token is about)</Typography></li>
                        <li><Typography variant="body2"><strong>aud:</strong> Audience (who should accept it)</Typography></li>
                        <li><Typography variant="body2"><strong>exp:</strong> Expiration time</Typography></li>
                        <li><Typography variant="body2"><strong>iat:</strong> Issued at time</Typography></li>
                        <li><Typography variant="body2"><strong>nbf:</strong> Not before time</Typography></li>
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', backgroundColor: 'rgba(232, 245, 233, 0.3)' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main' }}>AZURE AD CLAIMS</Typography>
                      <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
                        <li><Typography variant="body2"><strong>tid:</strong> Tenant ID</Typography></li>
                        <li><Typography variant="body2"><strong>oid:</strong> Object ID (user/app)</Typography></li>
                        <li><Typography variant="body2"><strong>upn:</strong> User Principal Name</Typography></li>
                        <li><Typography variant="body2"><strong>scp:</strong> Scopes (permissions)</Typography></li>
                        <li><Typography variant="body2"><strong>roles:</strong> Application roles</Typography></li>
                        <li><Typography variant="body2"><strong>wids:</strong> Directory roles</Typography></li>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  How to Use This Tool:
                </Typography>
                <Box component="ol" sx={{ pl: 3, '& li': { mb: 1 } }}>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Paste your JWT token</strong> into the input field (tokens typically start with "eyJ")
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Click "Decode Token"</strong> to parse and analyze the token structure
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Review the Token Summary</strong> for quick insights into token validity and key claims
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Use Token Analysis tab</strong> to classify token type, fetch JWKS, and verify signatures
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Click on any claim</strong> in the Interactive Claims Explorer for detailed explanations
                    </Typography>
                  </li>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Signature Verification:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  The Token Analysis tab allows you to verify token signatures using JWKS (JSON Web Key Set):
                </Typography>
                <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                  <li><Typography variant="body2" color="text.secondary">Click <strong>"Fetch from iss"</strong> to automatically retrieve JWKS from the issuer's discovery endpoint</Typography></li>
                  <li><Typography variant="body2" color="text.secondary">Or manually paste JWKS JSON and click <strong>"Parse JWKS"</strong></Typography></li>
                  <li><Typography variant="body2" color="text.secondary">Click <strong>"Verify Signature"</strong> to cryptographically verify the token hasn't been tampered with</Typography></li>
                </Box>
                <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
                  All decoding and verification happens entirely in your browser - tokens are never sent to any server.
                </Alert>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
                  Security Considerations:
                </Typography>
                <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
                  <li><Typography variant="body2" color="text.secondary">Always verify token expiration (exp claim) before accepting a token</Typography></li>
                  <li><Typography variant="body2" color="text.secondary">For ID tokens, ensure the audience (<strong>aud</strong>) matches your application's client ID. For access tokens, the audience (<strong>aud</strong>) must match the API's URI identifier.</Typography></li>
                  <li><Typography variant="body2" color="text.secondary">Verify the issuer (iss) matches your expected Azure AD tenant</Typography></li>
                  <li><Typography variant="body2" color="text.secondary">Check for high-privilege claims like directory roles (wids) - these require extra scrutiny</Typography></li>
                  <li><Typography variant="body2" color="text.secondary">Never paste production tokens with sensitive data unless necessary for debugging</Typography></li>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Token Decoder vs OIDC Metadata Inspector:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', backgroundColor: 'rgba(227, 242, 253, 0.3)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
                        Use Token Decoder when:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2.5, mb: 0, '& li': { mb: 0.5 } }}>
                        <li><Typography variant="body2">You already have a JWT token to analyze</Typography></li>
                        <li><Typography variant="body2">You need quick claim inspection</Typography></li>
                        <li><Typography variant="body2">You want to verify expiration status</Typography></li>
                        <li><Typography variant="body2">You need to check scopes and roles</Typography></li>
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', backgroundColor: 'rgba(232, 245, 233, 0.3)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main', mb: 1 }}>
                        Use OIDC Metadata Inspector when:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2.5, mb: 0, '& li': { mb: 0.5 } }}>
                        <li><Typography variant="body2">You need to validate issuer configuration</Typography></li>
                        <li><Typography variant="body2">You want to compare tenant setups</Typography></li>
                        <li><Typography variant="body2">You're troubleshooting B2C policies</Typography></li>
                        <li><Typography variant="body2">You need JWKS inspection before having tokens</Typography></li>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Quick tip:</Typography>
                  <Typography variant="body2">
                    Have a token already? Start here. Need to understand or validate the issuer first? Use the OIDC Metadata Inspector.
                  </Typography>
                </Alert>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Card>
    </Container>
  );
};

export default TokenDecoder;